<script>
/**
 * @component SnomedCodingInput
 * @description UC-REG-288 — Campo "Estudio" de la orden de imagen cuando la
 *              organización usa el esquema SNOMED CT. El médico escribe en
 *              lenguaje natural ("eco de panza"), el API devuelve una
 *              sugerencia con confianza y alternativas, y el médico:
 *                (A) confirma la sugerencia → `status: 'coded'`;
 *                (B) elige otra (alternativas o búsqueda directa) → `coded`;
 *                (C) deja el texto libre → `status: 'pending'` (pendiente de
 *                    codificar para el admin médico). Nunca queda trabado.
 *              En modo `snomed-search` no hay IA: la sugerencia sale de la
 *              búsqueda directa y siempre se pide confirmación.
 *
 *              v-model: null | { text, sctid, display, fsn, status, confidence, decisionId, layer }
 * @example <SnomedCodingInput v-model="question.value" :mode="'snomed-ai'" />
 */
import snomedCodingService from "@/services/snomed-coding";

export default {
  name: "SnomedCodingInput",
  props: {
    value: { type: Object, default: null },
    /** @description 'snomed-search' | 'snomed-ai' */
    mode: { type: String, default: "snomed-search" },
    isDisabled: { type: Boolean, default: false },
  },

  data() {
    return {
      text: (this.value && this.value.text) || "",
      isResolving: false,
      result: null,
      error: null,
      showAlternatives: false,
      altSearch: "",
      altResults: [],
      isSearching: false,
    };
  },

  computed: {
    /** @returns {boolean} hay un valor confirmado (concepto o texto libre) */
    isResolved() {
      return Boolean(
        this.value &&
          (this.value.status === "coded" || this.value.status === "pending")
      );
    },
    /** @returns {string} etiqueta de la capa que resolvió, para el médico */
    layerLabel() {
      const layer = this.result && this.result.layer;
      if (layer === "cache") return "ya resuelto antes";
      if (layer === "llm") return "sugerido por IA";
      if (layer === "semantic") return "búsqueda semántica";
      if (layer === "lexical") return "coincidencia de término";
      return "";
    },
    /** @returns {number|null} confianza en porcentaje */
    confidencePct() {
      const c =
        this.result &&
        this.result.suggestion &&
        this.result.suggestion.confidence;
      return typeof c === "number" ? Math.round(c * 100) : null;
    },
    /** @returns {Array} alternativas a mostrar: las del resultado o la búsqueda manual */
    alternativesToShow() {
      return this.altSearch
        ? this.altResults
        : (this.result && this.result.alternatives) || [];
    },
  },

  methods: {
    /**
     * @description Resuelve el texto escrito contra el API.
     * @returns {Promise<void>}
     */
    async resolve() {
      const text = (this.text || "").trim();
      if (!text || this.isResolving) return;
      this.isResolving = true;
      this.error = null;
      this.result = null;
      this.showAlternatives = false;
      this.altSearch = "";
      this.altResults = [];
      this.$emit("input", null);
      try {
        this.result = await snomedCodingService.resolve(text);
        if (this.result.status === "none") this.showAlternatives = true;
      } catch (e) {
        this.error =
          "No se pudo consultar el catálogo SNOMED. Podés dejar el texto libre.";
      } finally {
        this.isResolving = false;
      }
    },

    /**
     * @description Pre-carga un término (sugerencia de la IA de consulta) y lo resuelve.
     *              Mismo contrato que `ImagesSelect.triggerAISearch`.
     * @param {string} term
     */
    triggerAISearch(term) {
      if (!term) return;
      this.text = term;
      this.resolve();
    },

    /**
     * @description (A)/(B): el médico elige un concepto.
     * @param {Object} candidate - { sctid, display, fsn, confidence? }
     */
    async chooseConcept(candidate) {
      if (!candidate || !candidate.sctid) return;
      const decisionId = this.result && this.result.decisionId;
      const chosen = {
        text: this.text.trim(),
        sctid: candidate.sctid,
        display: candidate.display,
        fsn: candidate.fsn,
        status: "coded",
        confidence:
          this.result &&
          this.result.suggestion &&
          this.result.suggestion.sctid === candidate.sctid
            ? this.result.suggestion.confidence
            : 1,
        decisionId,
        layer: this.result && this.result.layer,
      };
      this.$emit("input", chosen);
      this.showAlternatives = false;
      if (decisionId) {
        try {
          await snomedCodingService.confirm(decisionId, {
            sctid: candidate.sctid,
          });
        } catch (e) {
          // La auditoría es best-effort: la orden se guarda igual.
        }
      }
    },

    /**
     * @description (C): el médico deja el texto libre → pendiente de codificar.
     */
    async keepFreeText() {
      const text = this.text.trim();
      if (!text) return;
      const decisionId = this.result && this.result.decisionId;
      this.$emit("input", { text, status: "pending", decisionId });
      this.showAlternatives = false;
      if (decisionId) {
        try {
          await snomedCodingService.confirm(decisionId, {});
        } catch (e) {
          // best-effort
        }
      }
    },

    /** @description Vuelve al estado editable (cambiar el estudio elegido). */
    reset() {
      this.$emit("input", null);
      this.result = null;
      this.error = null;
      this.showAlternatives = false;
      this.altSearch = "";
      this.altResults = [];
    },

    /**
     * @description Búsqueda directa para "Elegir otra" (sin IA).
     */
    async searchAlternatives() {
      const q = (this.altSearch || "").trim();
      if (!q) {
        this.altResults = [];
        return;
      }
      this.isSearching = true;
      try {
        this.altResults = await snomedCodingService.searchConcepts(q, 10);
      } catch (e) {
        this.altResults = [];
      } finally {
        this.isSearching = false;
      }
    },
  },
};
</script>

<template lang="pug">
.snomed-coding
  label.snomed-coding__label Estudio
    span.snomed-coding__badge SNOMED CT
  //- Valor resuelto: concepto confirmado o texto libre pendiente
  .snomed-coding__resolved(v-if="isResolved")
    .snomed-coding__resolved-main
      template(v-if="value.status === 'coded'")
        strong {{ value.display }}
        span.snomed-coding__sctid  · {{ value.sctid }}
      template(v-else)
        strong {{ value.text }}
        span.snomed-coding__pending  · pendiente de codificar
    button.snomed-coding__link(type="button" v-if="!isDisabled" @click="reset") Cambiar

  template(v-else)
    .snomed-coding__row
      el-input(
        v-model="text"
        :disabled="isDisabled || isResolving"
        placeholder="Escribí el estudio, ej. eco de panza"
        @keyup.enter.native="resolve"
      )
      el-button(type="primary" :loading="isResolving" :disabled="isDisabled || !text.trim()" @click="resolve")
        | {{ mode === 'snomed-ai' ? 'Buscar con IA' : 'Buscar' }}

    p.snomed-coding__error(v-if="error") {{ error }}

    //- Sugerencia principal
    .snomed-coding__suggestion(v-if="result && result.suggestion")
      .snomed-coding__suggestion-text
        strong {{ result.suggestion.display }}
        span.snomed-coding__sctid  · {{ result.suggestion.sctid }}
        .snomed-coding__meta
          span(v-if="confidencePct !== null") Confianza {{ confidencePct }}%
          span(v-if="layerLabel")  · {{ layerLabel }}
      .snomed-coding__ambiguity(v-if="result.ambiguity") {{ result.ambiguity.question }}
      .snomed-coding__actions
        el-button(type="success" size="small" :disabled="isDisabled" @click="chooseConcept(result.suggestion)") Confirmar
        el-button(size="small" :disabled="isDisabled" @click="showAlternatives = !showAlternatives") Elegir otra
        el-button(size="small" type="text" :disabled="isDisabled" @click="keepFreeText") Dejar como texto

    //- Sin candidatos
    .snomed-coding__none(v-if="result && result.status === 'none'")
      p No encontramos un estudio SNOMED para "{{ text }}".
      .snomed-coding__actions
        el-button(size="small" type="text" :disabled="isDisabled" @click="keepFreeText") Dejar como texto (pendiente de codificar)

    //- Alternativas + búsqueda directa
    .snomed-coding__alternatives(v-if="showAlternatives && result")
      .snomed-coding__row
        el-input(v-model="altSearch" size="small" placeholder="Buscar otro estudio" @keyup.enter.native="searchAlternatives" clearable)
        el-button(size="small" :loading="isSearching" @click="searchAlternatives") Buscar
      ul.snomed-coding__list
        li(v-for="alt in alternativesToShow" :key="alt.sctid")
          button.snomed-coding__option(type="button" :disabled="isDisabled" @click="chooseConcept(alt)")
            span {{ alt.display }}
            span.snomed-coding__sctid  · {{ alt.sctid }}
        li.snomed-coding__empty(v-if="!alternativesToShow.length && !isSearching") Sin resultados
</template>

<style scoped lang="scss">
.snomed-coding {
  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    margin-bottom: 6px;
  }
  &__badge {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding: 2px 6px;
    border-radius: 10px;
    background: #e6f4f1;
    color: #0b7a75;
  }
  &__row {
    display: flex;
    gap: 8px;
    align-items: center;
    .el-input {
      flex: 1;
    }
  }
  &__error {
    color: #c0392b;
    font-size: 12px;
    margin: 6px 0 0;
  }
  &__suggestion,
  &__none,
  &__resolved {
    margin-top: 10px;
    padding: 10px 12px;
    border: 1px solid #dfe6e9;
    border-radius: 8px;
    background: #fafbfc;
  }
  &__resolved {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  &__meta {
    font-size: 12px;
    color: #636e72;
    margin-top: 2px;
  }
  &__sctid {
    color: #636e72;
    font-size: 12px;
  }
  &__pending {
    color: #b7791f;
    font-size: 12px;
  }
  &__ambiguity {
    margin-top: 6px;
    font-size: 13px;
    color: #b7791f;
  }
  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  &__alternatives {
    margin-top: 10px;
  }
  &__list {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
    max-height: 220px;
    overflow: auto;
  }
  &__option {
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 6px 4px;
    cursor: pointer;
    border-radius: 4px;
    &:hover {
      background: #eef3f7;
    }
  }
  &__empty {
    color: #636e72;
    font-size: 12px;
    padding: 6px 4px;
  }
  &__link {
    background: transparent;
    border: 0;
    color: #0b7a75;
    cursor: pointer;
    font-weight: 600;
  }
}
</style>
